import { useTickets } from "../../contexts/TicketContext";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useMemo } from "react";
import TicketCard from "./ticketCard";
import { Icon } from "@iconify/react";

import TicketPriorityChart from './charts/TicketPriorityChart';
import TotalTickets from './charts/TotalTicketsChart';
import ResolvedDonutChart from './charts/ResolvedChart';
import { NewTickets } from './charts/NewTicketsChart';
import { YourTicketsChart } from './charts/YourTicketsChart';

export const TicketSummary = () => {
    const { tickets } = useTickets();
    const user = useAuthUser();
    
    // Filter tickets based on user role
    const filteredTickets = useMemo(() => {
        if (!user || !tickets) return tickets;
        
        // If user is client_user, only show tickets created by them
        if (user.role === 'client_user' || user.roles?.includes('client_user')) {
            return tickets.filter(ticket => ticket.email === user.email);
        }
        
        // For other roles (client_admin, helios_admin, helios_user), show all tickets (existing logic)
        return tickets;
    }, [tickets, user]);
    
    const total = filteredTickets.length;
    const resolved = filteredTickets.filter(ticket => ticket.status === 'Closed').length;

    return(
        <div className="tickets-summary-responsive">
            {/* Small Charts Section */}
            <div className="flex flex-col gap-3 col-span-3">
                <div className="w-full">
                    <YourTicketsChart tickets={filteredTickets} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <TicketPriorityChart tickets={filteredTickets} />
                    <NewTickets tickets={filteredTickets} />
                </div>
            </div>
            {/* Total Tickets Chart */}
            <div className="flex flex-col sm:flex-row gap-3 items-center w-full col-span-3 xl:col-span-2">
                <TotalTickets tickets={filteredTickets} />
                {/* Resolved Tickets Donut */}
                <div className="flex flex-col items-center card-container justify-center gap-2 p-3 rounded-md sm:p-0 w-full h-full">
                    <ResolvedDonutChart resolved={resolved} total={total} color="#00B8FF" size={150} />
                    <p className="text-center text-sm sm:text-base">
                        <span className="text-primary text-lg sm:text-xl font-bold">{resolved}</span> / {total} tickets resolved
                    </p>
                </div>
            </div>
        </div>
    );
}

// Update this to show your tickets specifically

export const YourTickets = ({ selectedTicket, setSelectedTicket }) => {
    const { tickets } = useTickets();
    const user = useAuthUser();
    
    // Filter tickets based on user role
    const filteredTickets = useMemo(() => {
        if (!user || !tickets) return tickets;
        
        // If user is helios admin, show only open tickets
        if (user.role === 'helios_admin' || user.roles?.includes('helios_admin')) {
            return tickets.filter(ticket => ticket.status === 'Open');
        }
        
        // If user is client_user, only show tickets created by them
        if (user.role === 'client_user' || user.roles?.includes('client_user')) {
            return tickets.filter(ticket => ticket.email === user.email);
        }
        
        // For other roles (client_admin, helios_user), show all tickets (existing logic)
        return tickets;
    }, [tickets, user]);

    // Check if user is helios admin
    const isHeliosAdmin = user?.role === 'helios_admin' || user?.roles?.includes('helios_admin');
    
    // Show default state for helios admin with no open tickets
    if (isHeliosAdmin && filteredTickets.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center w-full text-center text-gray-400">
                <Icon icon="carbon:no-ticket" className="text-6xl mb-4" />
                <p className="text-lg">No open tickets</p>
            </div>
        );
    }

    return(
        <div className="tickets-grid-responsive w-full">
            {filteredTickets
            .slice(0, 6)
            .map((ticket) => (
                <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onClick={() => setSelectedTicket(ticket)}
                    selectedTicket={selectedTicket}
                />
            ))}
        </div>
    );
}
